import { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  createSurvey,
  getSurvey,
  updateSurvey,
  FLOW_ID_REGEX,
  type Survey,
  type SurveyCreate,
  type SurveyPatch,
  type SurveyStatus,
} from '../api/surveys.js';
import { listChannels } from '../api/channels.js';
import { ApiError } from '../api/apiClient.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';

const STATUSES: SurveyStatus[] = ['active', 'paused', 'archived'];

interface FormValues {
  name: string;
  description: string;
  flowId: string;
  channelId: string;
  status: SurveyStatus;
  questionsCount: number;
  responsesCount: number;
  averageScore: string;
  startsAt: string;
  endsAt: string;
}

const emptyValues: FormValues = {
  name: '',
  description: '',
  flowId: '',
  channelId: '',
  status: 'active',
  questionsCount: 0,
  responsesCount: 0,
  averageScore: '',
  startsAt: '',
  endsAt: '',
};

export const surveyFormSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().max(500).optional(),
  flowId: z.string().regex(FLOW_ID_REGEX).min(1).max(120),
  channelId: z.string().optional(),
  status: z.enum(['active', 'paused', 'archived']),
  questionsCount: z.number().int().min(0),
  responsesCount: z.number().int().min(0),
  averageScore: z.number().min(0).max(10).optional(),
  startsAt: z.string().optional(),
  endsAt: z.string().optional(),
});

function toIsoOrUndef(v: string): string | undefined {
  if (!v) return undefined;
  // datetime-local viene como "YYYY-MM-DDTHH:mm" — completá con :00 y Z naive.
  return v.length === 16 ? `${v}:00Z` : v;
}

function fromIso(v: string | undefined): string {
  if (!v) return '';
  // Recortá a "YYYY-MM-DDTHH:mm" para datetime-local.
  return v.slice(0, 16);
}

function valuesToCreate(v: FormValues): SurveyCreate {
  const out: SurveyCreate = {
    name: v.name.trim(),
    flowId: v.flowId.trim(),
    status: v.status,
  };
  if (v.description.trim()) out.description = v.description.trim();
  if (v.channelId) out.channelId = v.channelId;
  if (v.questionsCount > 0) out.questionsCount = v.questionsCount;
  if (v.responsesCount > 0) out.responsesCount = v.responsesCount;
  const avg = v.averageScore.trim() ? Number(v.averageScore) : NaN;
  if (Number.isFinite(avg)) out.averageScore = avg;
  const sa = toIsoOrUndef(v.startsAt);
  if (sa) out.startsAt = sa;
  const ea = toIsoOrUndef(v.endsAt);
  if (ea) out.endsAt = ea;
  return out;
}

function buildPatch(current: Survey, v: FormValues): SurveyPatch {
  const patch: SurveyPatch = {};
  const name = v.name.trim();
  if (name !== current.name) patch.name = name;
  const desc = v.description.trim() || undefined;
  if (desc !== (current.description ?? undefined)) patch.description = desc;
  const flow = v.flowId.trim();
  if (flow !== current.flowId) patch.flowId = flow;
  const ch = v.channelId || undefined;
  if (ch !== (current.channelId ?? undefined)) patch.channelId = ch;
  if (v.status !== current.status) patch.status = v.status;
  if (v.questionsCount !== current.questionsCount) patch.questionsCount = v.questionsCount;
  if (v.responsesCount !== current.responsesCount) patch.responsesCount = v.responsesCount;
  const avgTrim = v.averageScore.trim();
  const avg = avgTrim ? Number(avgTrim) : undefined;
  if (avg !== (current.averageScore ?? undefined)) patch.averageScore = avg;
  const sa = toIsoOrUndef(v.startsAt);
  if (sa !== (current.startsAt ?? undefined)) patch.startsAt = sa;
  const ea = toIsoOrUndef(v.endsAt);
  if (ea !== (current.endsAt ?? undefined)) patch.endsAt = ea;
  return patch;
}

export interface SurveyFormProps {
  mode: 'create' | 'edit';
}

export function SurveyForm({ mode }: SurveyFormProps): JSX.Element {
  const { id = '', surveyId = '' } = useParams<{ id: string; surveyId?: string }>();
  const navigate = useNavigate();
  const pushToast = useUiStore((s) => s.pushToast);
  const queryClient = useQueryClient();

  const surveyQuery = useQuery({
    queryKey: ['survey', id, surveyId],
    queryFn: () => getSurvey(id, surveyId),
    enabled: mode === 'edit' && Boolean(id && surveyId),
  });

  const channelsQuery = useQuery({
    queryKey: ['channels', id, { includeArchived: false }],
    queryFn: () => listChannels(id),
    enabled: Boolean(id),
  });

  const defaultValues = useMemo<FormValues>(() => {
    if (mode === 'edit' && surveyQuery.data) {
      const s = surveyQuery.data;
      return {
        name: s.name,
        description: s.description ?? '',
        flowId: s.flowId,
        channelId: s.channelId ?? '',
        status: s.status,
        questionsCount: s.questionsCount,
        responsesCount: s.responsesCount,
        averageScore: typeof s.averageScore === 'number' ? String(s.averageScore) : '',
        startsAt: fromIso(s.startsAt),
        endsAt: fromIso(s.endsAt),
      };
    }
    return emptyValues;
  }, [mode, surveyQuery.data]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const createMutation = useMutation({
    mutationFn: (body: SurveyCreate) => createSurvey(id, body),
    onSuccess: () => {
      pushToast('success', t.survey.created);
      queryClient.invalidateQueries({ queryKey: ['surveys', id] });
      navigate(`/tenants/${id}/surveys`);
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (patch: SurveyPatch) => {
      const current = surveyQuery.data;
      if (!current) throw new Error('survey_not_loaded');
      return updateSurvey(id, current.id, patch, current.version);
    },
    onSuccess: () => {
      pushToast('success', t.survey.updated);
      queryClient.invalidateQueries({ queryKey: ['surveys', id] });
      queryClient.invalidateQueries({ queryKey: ['survey', id, surveyId] });
      navigate(`/tenants/${id}/surveys`);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.status === 412) {
        pushToast('error', t.survey.versionConflict);
        return;
      }
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const onSubmit = handleSubmit((values) => {
    const avgTrim = values.averageScore.trim();
    const candidate = {
      ...values,
      averageScore: avgTrim ? Number(avgTrim) : undefined,
    };
    const parsed = surveyFormSchema.safeParse(candidate);
    if (!parsed.success) {
      pushToast('error', parsed.error.issues[0]?.message ?? 'invalid');
      return;
    }
    if (mode === 'create') {
      createMutation.mutate(valuesToCreate(values));
    } else if (surveyQuery.data) {
      updateMutation.mutate(buildPatch(surveyQuery.data, values));
    }
  });

  if (mode === 'edit' && surveyQuery.isLoading) return <p>{t.app.loading}</p>;
  if (mode === 'edit' && surveyQuery.error) {
    return <p className="text-red-700">{(surveyQuery.error as Error).message}</p>;
  }

  const channels = channelsQuery.data ?? [];
  const submitting = isSubmitting || createMutation.isPending || updateMutation.isPending;

  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold">
        {mode === 'create' ? t.survey.newTitle : t.survey.editTitle}
      </h2>
      <form
        onSubmit={onSubmit}
        className="space-y-4 rounded border border-slate-200 bg-white p-5"
        noValidate
      >
        <Field label={t.survey.fields.name} error={errors.name?.message}>
          <input
            type="text"
            {...register('name', {
              required: t.survey.nameRequired,
              maxLength: { value: 120, message: t.survey.nameTooLong },
            })}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </Field>

        <Field label={t.survey.fields.description} error={errors.description?.message}>
          <textarea
            rows={3}
            {...register('description', {
              maxLength: { value: 500, message: t.survey.descriptionTooLong },
            })}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </Field>

        <Field
          label={t.survey.fields.flowId}
          error={errors.flowId?.message}
          helper={t.survey.flowIdHelper}
        >
          <input
            type="text"
            {...register('flowId', {
              required: t.survey.flowIdRequired,
              validate: (v) => FLOW_ID_REGEX.test(v) || t.survey.flowIdInvalid,
            })}
            placeholder="nps-onboarding-v1"
            className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs"
          />
        </Field>

        <Field label={t.survey.fields.channel} error={errors.channelId?.message}>
          <select
            {...register('channelId')}
            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            <option value="">{t.survey.channelOptional}</option>
            {channels.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.channelType})
              </option>
            ))}
          </select>
        </Field>

        <Field label={t.survey.fields.status} error={errors.status?.message}>
          <select
            {...register('status')}
            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t.survey.status[s]}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label={t.survey.fields.questionsCount} error={errors.questionsCount?.message}>
            <input
              type="number"
              min={0}
              {...register('questionsCount', { valueAsNumber: true })}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </Field>
          <Field label={t.survey.fields.responsesCount} error={errors.responsesCount?.message}>
            <input
              type="number"
              min={0}
              {...register('responsesCount', { valueAsNumber: true })}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </Field>
          <Field
            label={t.survey.fields.averageScore}
            error={errors.averageScore?.message}
            helper="0..10"
          >
            <input
              type="number"
              step="0.01"
              min={0}
              max={10}
              {...register('averageScore', {
                validate: (v) => {
                  if (!v) return true;
                  const n = Number(v);
                  if (!Number.isFinite(n) || n < 0 || n > 10) return t.survey.averageScoreRange;
                  return true;
                },
              })}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t.survey.fields.startsAt} error={errors.startsAt?.message}>
            <input
              type="datetime-local"
              {...register('startsAt')}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </Field>
          <Field label={t.survey.fields.endsAt} error={errors.endsAt?.message}>
            <input
              type="datetime-local"
              {...register('endsAt')}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </Field>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Link
            to={`/tenants/${id}/surveys`}
            className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-100"
          >
            {t.app.cancel}
          </Link>
          <button
            type="submit"
            disabled={submitting}
            className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {submitting ? '…' : t.app.save}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  error,
  helper,
  children,
}: {
  label: string;
  error?: string;
  helper?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-slate-700">{label}</span>
      {children}
      {helper && !error && <span className="mt-1 block text-xs text-slate-500">{helper}</span>}
      {error && (
        <span role="alert" className="mt-1 block text-xs text-red-600">
          {error}
        </span>
      )}
    </label>
  );
}
